import React, { useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Flame, Zap, Fuel, Snowflake, ArrowRight, CheckCircle2, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOURCES = [
  { id: "fioul", label: "Fioul", icon: <Fuel className="h-4 w-4" />, saving: 0.72 },
  { id: "gaz", label: "Gaz naturel", icon: <Flame className="h-4 w-4" />, saving: 0.55 },
  { id: "elec", label: "Électrique (convecteurs)", icon: <Zap className="h-4 w-4" />, saving: 0.65 },
  { id: "pac_old", label: "Ancienne PAC", icon: <Snowflake className="h-4 w-4" />, saving: 0.30 },
];

const INSTALL_COST = 12000;
const AIDES_MAX = 8000;

const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n));

export default function PACSimulator() {
  const [source, setSource] = useState("fioul");
  const [bill, setBill] = useState(2400);
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", postcode: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const src = SOURCES.find((s) => s.id === source);
  const saving = src.saving;

  const results = useMemo(() => {
    const newCost = Math.round(bill * (1 - saving));
    const annualSavings = bill - newCost;
    const netInvest = INSTALL_COST - AIDES_MAX;
    const payback = annualSavings > 0 ? (netInvest / annualSavings).toFixed(1) : "—";
    const savings10y = annualSavings * 10 - netInvest;
    return { newCost, annualSavings, netInvest, payback, savings10y };
  }, [bill, saving]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Merci de renseigner nom et email.");
      return;
    }
    setSubmitting(true);
    const message = [
      "── Simulation PAC ──",
      `Chauffage actuel : ${src.label}`,
      `Facture chauffage annuelle : ${fmt(bill)} €`,
      `Économie estimée : ${fmt(results.annualSavings)} € / an`,
      `Nouvelle facture : ${fmt(results.newCost)} € / an`,
      `Reste à charge PAC : ~${fmt(results.netInvest)} €`,
      `Amortissement : ${results.payback} ans`,
      `Gain 10 ans : ${fmt(Math.max(0, results.savings10y))} €`,
      "",
      "Merci de me rappeler pour un devis PAC personnalisé.",
    ].join("\n");

    try {
      await axios.post(`${API}/quote-requests`, {
        ...form,
        profile: "particulier",
        interest: "pac",
        message,
      });
      setSent(true);
      toast.success("Demande envoyée ! Rappel sous 24 h ouvrées.");
    } catch (err) {
      toast.error("Erreur d'envoi. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-14 lg:mt-20" data-testid="pac-simulator">
      <div className="eyebrow mb-4">Simulateur d'économies · PAC</div>
      <h3 className="font-serif-display text-3xl lg:text-5xl leading-[1] tracking-tight text-[#0F172A] max-w-2xl balance">
        Combien vais-je économiser ?
      </h3>

      <div className="mt-8 lg:mt-10 grid lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Inputs */}
        <div className="bg-[#F5F1EA] border border-[#e2d9ce] rounded-2xl p-6 lg:p-8">
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#64748B] mb-3">
              Chauffage actuel
            </div>
            <div className="grid grid-cols-2 gap-2">
              {SOURCES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSource(s.id)}
                  data-testid={`pac-source-${s.id}`}
                  className={`flex items-center gap-2 p-3 rounded-xl text-sm text-left border transition-all ${
                    source === s.id
                      ? "bg-[#0F172A] text-[#FDFBF7] border-[#0F172A]"
                      : "bg-[#FDFBF7] text-[#0F172A] border-[#e2d9ce] hover:border-[#0F172A]"
                  }`}
                >
                  <span className={source === s.id ? "text-[#FF9800]" : "text-[#2D7A4D]"}>
                    {s.icon}
                  </span>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#64748B]">
                Facture chauffage annuelle
              </div>
              <div className="font-serif-display text-3xl text-[#0F172A]">{fmt(bill)} €</div>
            </div>
            <input
              data-testid="pac-bill-input"
              type="range"
              min={500}
              max={5000}
              step={50}
              value={bill}
              onChange={(e) => setBill(Number(e.target.value))}
              className="w-full"
              style={{ accentColor: "#2D7A4D" }}
            />
            <div className="flex justify-between mt-1 font-mono-tech text-[10px] text-[#94a3b8] tracking-[0.15em]">
              <span>500 €</span>
              <span>5 000 €</span>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-[#e2d9ce] text-xs text-[#64748B] leading-relaxed">
            Estimation indicative basée sur un COP moyen de 4 et les aides
            MaPrimeRénov' + CEE. Un chiffrage précis nécessite une visite technique.
          </div>
        </div>

        {/* Results */}
        <div className="bg-[#0F172A] text-[#FDFBF7] rounded-2xl p-6 lg:p-8 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(#fdfbf7 1px, transparent 1px), linear-gradient(90deg, #fdfbf7 1px, transparent 1px)",
              backgroundSize: "36px 36px",
            }}
          />
          <div className="relative">
            {!sent && (
              <>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#FDFBF7]/60">
                  Vos économies estimées
                </div>
                <motion.div
                  key={results.annualSavings}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="mt-3"
                >
                  <div className="flex items-baseline gap-2">
                    <span className="font-serif-display text-[56px] lg:text-[72px] text-[#FF9800] leading-none">
                      {fmt(results.annualSavings)}
                    </span>
                    <span className="font-serif-display text-2xl text-[#FF9800]">€ / an</span>
                  </div>
                </motion.div>

                <div className="mt-6 grid grid-cols-2 gap-4">
                  <MiniKpi label="Nouvelle facture" value={`${fmt(results.newCost)} €`} />
                  <MiniKpi label="Reste à charge" value={`~${fmt(results.netInvest)} €`} />
                  <MiniKpi label="Amortissement" value={`${results.payback} ans`} />
                  <MiniKpi
                    label="Gain 10 ans"
                    value={`+${fmt(Math.max(0, results.savings10y))} €`}
                    accent
                  />
                </div>
              </>
            )}

            {/* Direct form */}
            <AnimatePresence mode="wait">
              {sent ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-6"
                  data-testid="pac-success"
                >
                  <div className="h-14 w-14 rounded-full bg-[#4CAF50]/20 border border-[#4CAF50] flex items-center justify-center mx-auto">
                    <CheckCircle2 className="h-7 w-7 text-[#4CAF50]" />
                  </div>
                  <h4 className="font-serif-display text-2xl mt-4">Demande envoyée</h4>
                  <p className="text-sm text-[#FDFBF7]/70 mt-2 max-w-xs mx-auto">
                    Un conseiller HMCD vous rappelle sous 24 h ouvrées avec votre chiffrage PAC personnalisé.
                  </p>
                  <button
                    onClick={() => {
                      setSent(false);
                      setExpanded(false);
                      setForm({ name: "", email: "", phone: "", postcode: "" });
                    }}
                    className="mt-6 text-xs font-mono-tech uppercase tracking-[0.22em] text-[#FF9800] hover:text-[#FDFBF7]"
                    data-testid="pac-new-simulation"
                  >
                    Nouvelle simulation
                  </button>
                </motion.div>
              ) : expanded ? (
                <motion.form
                  key="form"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  onSubmit={submit}
                  className="mt-6 pt-6 border-t border-[#FDFBF7]/15 space-y-3"
                  data-testid="pac-quick-form"
                >
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#FF9800]">
                    Devis PAC en 1 clic
                  </div>
                  <p className="text-xs text-[#FDFBF7]/70 -mt-1 mb-2">
                    Simulation pré-remplie — laissez juste vos coordonnées.
                  </p>
                  <input
                    data-testid="pac-form-name"
                    type="text"
                    required
                    placeholder="Nom complet *"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-[#FDFBF7]/[0.06] border border-[#FDFBF7]/20 rounded-lg px-3 py-2.5 text-sm text-[#FDFBF7] placeholder:text-[#FDFBF7]/40 focus:outline-none focus:border-[#FF9800]"
                  />
                  <input
                    data-testid="pac-form-email"
                    type="email"
                    required
                    placeholder="Email *"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-[#FDFBF7]/[0.06] border border-[#FDFBF7]/20 rounded-lg px-3 py-2.5 text-sm text-[#FDFBF7] placeholder:text-[#FDFBF7]/40 focus:outline-none focus:border-[#FF9800]"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      data-testid="pac-form-phone"
                      type="tel"
                      placeholder="Téléphone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full bg-[#FDFBF7]/[0.06] border border-[#FDFBF7]/20 rounded-lg px-3 py-2.5 text-sm text-[#FDFBF7] placeholder:text-[#FDFBF7]/40 focus:outline-none focus:border-[#FF9800]"
                    />
                    <input
                      data-testid="pac-form-postcode"
                      type="text"
                      placeholder="Code postal"
                      value={form.postcode}
                      onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                      className="w-full bg-[#FDFBF7]/[0.06] border border-[#FDFBF7]/20 rounded-lg px-3 py-2.5 text-sm text-[#FDFBF7] placeholder:text-[#FDFBF7]/40 focus:outline-none focus:border-[#FF9800]"
                    />
                  </div>
                  <div className="pt-2 flex items-center gap-2">
                    <button
                      data-testid="pac-form-submit"
                      type="submit"
                      disabled={submitting}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#FF9800] hover:bg-[#BF4300] transition-colors py-3 text-[#0F172A] font-semibold text-sm disabled:opacity-60"
                    >
                      {submitting ? "Envoi…" : "Envoyer ma simulation"}
                      <Send className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setExpanded(false)}
                      className="text-xs text-[#FDFBF7]/60 hover:text-[#FDFBF7] px-3"
                    >
                      Annuler
                    </button>
                  </div>
                  <p className="text-[10px] text-[#FDFBF7]/40 pt-1">
                    En envoyant ce formulaire vous acceptez d'être recontacté par HMCD. Aucune donnée transmise à des tiers.
                  </p>
                </motion.form>
              ) : (
                <motion.button
                  key="cta"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  data-testid="pac-simulator-cta"
                  onClick={() => setExpanded(true)}
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FF9800] hover:bg-[#BF4300] transition-colors py-3.5 text-[#0F172A] font-semibold text-[15px]"
                >
                  Obtenir mon devis PAC — 1 clic
                  <ArrowRight className="h-4 w-4" />
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

function MiniKpi({ label, value, accent }) {
  return (
    <div>
      <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#FDFBF7]/50">
        {label}
      </div>
      <div className={`font-serif-display text-xl lg:text-2xl mt-1.5 ${accent ? "text-[#4CAF50]" : "text-[#FDFBF7]"}`}>
        {value}
      </div>
    </div>
  );
}
